import i18n from 'i18next';
import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx';

import {
  EditFormStoreEvent,
  isEditFormStoreSetHighlightedRowsEvent,
  isEditFormStoreToggleSettingsDrawerEvent,
} from 'config/pbEvents';
import { CatalogFormFieldEntity } from 'entities/CatalogFormField';
import { FormFieldTableRow } from 'entities/Form';
import { FillingMethod } from 'entities/FormField';
import { FieldType, FormStatus } from 'entities/common';
import { ToggleStateStore } from 'stores/common';
import { ILocalStore } from 'stores/interfaces';

import { BasicFormStore } from '../BasicFormStore';
import { FormFieldModel } from '../models';
import { TableRowEntityType } from '../types';

import { EditFormStoreDataStore } from './EditFormStoreDataStore';
import { EditFormStoreMediator } from './EditFormStoreMediator';
import { FieldsCatalogControllerStore } from './FieldsCatalogControllerStore';
import { IEditFormStore } from './interface';

type PrivateFields =
  | '_initialize'
  | '_highlightedFieldIds'
  | '_canHandleSetupButtonClick'
  | '_handleSetHighLightedRowsEvent'
  | '_handleToggleSettingsDrawerToggle';

type EditFormStoreParams = {
  basicStore: BasicFormStore;
  mediator: EditFormStoreMediator;
};

type ButtonDisabledParams = {
  disabled: boolean;
  text: string;
};

/**
 * Стор для формы
 */
class EditFormStore implements ILocalStore, IEditFormStore {
  constructor({ mediator, basicStore }: EditFormStoreParams) {
    makeObservable<EditFormStore, PrivateFields>(this, {
      _highlightedFieldIds: observable.ref,
      highlightedFields: computed,
      displayRequiredFieldsWarning: computed,

      hasUnfilledFormulas: computed,
      hasUnsavedFields: computed,
      continueButtonDisabled: computed,

      mergeData: action.bound,

      deleteFormField: action.bound,
      _initialize: action.bound,
      updateForm: action.bound,

      handleFormFieldClick: action.bound,

      handleSetupButtonClick: action.bound,
      _handleSetHighLightedRowsEvent: action.bound,
      _handleToggleSettingsDrawerToggle: action.bound,
      _canHandleSetupButtonClick: observable,
      isFieldSetupDisabled: action.bound,
    });

    this.formStore = basicStore;
    this._mediator = mediator;

    this.dataModel = new EditFormStoreDataStore({
      editFormStore: this,
    });

    this._mediatorSubscribers.push(
      this._mediator.subscribeToSetHighlightedRowsEvent(
        this._handleSetHighLightedRowsEvent
      )
    );

    this._mediatorSubscribers.push(
      this._mediator.subscribeToToggleSettingsDrawerEvent(
        this._handleToggleSettingsDrawerToggle
      )
    );

    this._initialize();

    this._dataChangedDisposer = reaction(
      () => this.formStore.prevData,
      this._initialize
    );
  }

  private readonly _dataChangedDisposer: IReactionDisposer;

  readonly saveModal = new ToggleStateStore();

  readonly formStore: BasicFormStore;

  readonly dataModel: EditFormStoreDataStore;

  private readonly _mediator: EditFormStoreMediator;
  private readonly _mediatorSubscribers: VoidFunction[] = [];

  private _canHandleSetupButtonClick = true;

  readonly catalogDrawerControllerStore = new FieldsCatalogControllerStore();

  /** Список выбранных айдишников для выделения строчек в таблице*/
  private _highlightedFieldIds: string[] = [];

  /** Отображать плашку, если сейчас выбраны только обязательные поля */
  get displayRequiredFieldsWarning(): boolean {
    return !this.formStore.fieldsCollection
      .linearizeCollection((e) => e)
      .some((item) => {
        return !this.formStore.catalogStore.fieldsCollection[
          item.relatedCatalogFieldId
        ]?.required;
      });
  }

  get hasUnfilledFormulas(): boolean {
    return this.formStore.fieldsCollection
      .linearizeCollection((e) => e)
      .some((item) => {
        return item.hasUnfilledFormula;
      });
  }

  get hasUnsavedFields(): boolean {
    return this.formStore.fieldsCollection
      .linearizeCollection((e) => e)
      .some((item) => {
        return !item.saved;
      });
  }

  get continueButtonDisabled(): ButtonDisabledParams {
    if (this.hasUnfilledFormulas) {
      return {
        disabled: true,
        text: i18n.t('editForm.notFilledFormulas') as string,
      };
    }

    if (this.hasUnsavedFields) {
      return {
        disabled: true,
        text: i18n.t('editForm.unsavedFields') as string,
      };
    }

    return {
      disabled: false,
      text: '',
    };
  }

  get highlightedFields(): string[] {
    return this._highlightedFieldIds;
  }

  private _handleSetHighLightedRowsEvent(event: EditFormStoreEvent): void {
    if (isEditFormStoreSetHighlightedRowsEvent(event)) {
      this._highlightedFieldIds = event.payload.rows;
    }
  }

  /** Обрабатываем клики, только если не открыт один из дроверов настройки поля */
  private _handleToggleSettingsDrawerToggle(event: EditFormStoreEvent): void {
    if (isEditFormStoreToggleSettingsDrawerEvent(event)) {
      if (event.payload.payload) {
        this._canHandleSetupButtonClick = false;

        return;
      }

      this._canHandleSetupButtonClick = true;
    }
  }

  isFieldSetupDisabled({
    setupRequired,
    fillingMethod,
    dataType,
    importEditable,
  }: {
    setupRequired: boolean;
    fillingMethod: FillingMethod;
    dataType: FieldType;
    importEditable: boolean;
  }): boolean {
    if (!this.formStore.saved) {
      return true;
    }

    if (!setupRequired) {
      return true;
    }

    return (
      fillingMethod === FillingMethod.import &&
      dataType !== FieldType.formula &&
      !importEditable
    );
  }

  /**
   * Удаление строки в форме
   * @param field
   */
  deleteFormField(
    field: FormFieldTableRow<FormFieldModel, CatalogFormFieldEntity>
  ): void {
    this.formStore.fieldsCollection
      .deleteItemFromCollection(field.formField.id)
      .destroy();

    this.catalogDrawerControllerStore.selectField(field.catalogField.id, false);

    this.catalogDrawerControllerStore.init(
      this.formStore.fieldsCollection.linearizeCollection(
        (element) => element.relatedCatalogFieldId
      )
    );
  }

  /** Обрабатываем клик по строчке в форме*/
  handleFormFieldClick(field: TableRowEntityType): void {
    this._mediator.emitRowClickEvent(field);
  }

  /** Обрабатываем клик по кнопке "Настроить"*/
  handleSetupButtonClick(field: TableRowEntityType): void {
    /** Скипаем клики, если редактируем один из сторов */
    if (!this._canHandleSetupButtonClick) {
      return;
    }

    this._mediator.emitSetupButtonClickEvent(field);
  }

  /**
   * мердж данных при выборе полей из каталога
   */
  mergeData(): void {
    // Делаем массив полей
    const linearizedCollection =
      this.formStore.fieldsCollection.linearizeCollection((elem) => elem);

    const selectedFields =
      this.catalogDrawerControllerStore.saveSelectedFieldIds();

    this.catalogDrawerControllerStore.close();

    const selectedIndexesSet = new Set(selectedFields);

    // Пробегаемся по массиву полей. Если есть в set, то значит поле выбрано и с ним ничего не делаем. Если нету, то убираем.
    const newArr = linearizedCollection.filter((elem) => {
      if (selectedIndexesSet.has(elem.relatedCatalogFieldId)) {
        selectedIndexesSet.delete(elem.relatedCatalogFieldId);

        return true;
      }

      return false;
    });

    // Оставшиеся элементы сета свидетельствуют о том, что есть еще новые элементы. создаем их и добавляем в конец
    const arr = Array.from(selectedIndexesSet).map((index) => {
      const element = this.formStore.catalogStore.fieldsCollection[index];

      return FormFieldModel.createFromCatalog(this.formStore.id as number, {
        dataType: element.dataType,
        name: element.label,
        id: element.id,
      });
    });

    this.formStore.fieldsCollection.rebuildCollection(newArr.concat(arr));
  }

  /**
   * Инициализация стора
   * @private
   */
  private _initialize(): void {
    const data = this.formStore.fieldsCollection.linearizeCollection(
      (elem) => elem.toServerFormat
    );

    if (!data) {
      return;
    }

    this.catalogDrawerControllerStore.init(
      data.map((element) => element.field_data.id)
    );
  }

  resetChanges(): void {
    this.formStore.reset();

    /** Сбрасываем ранее выбранные поля в сторе каталога */
    this.catalogDrawerControllerStore.init(
      this.formStore.fieldsCollection.linearizeCollection(
        (element) => element.relatedCatalogFieldId
      )
    );
  }

  async handleSave(): Promise<void> {
    if (this.formStore.isAlreadySubmittedForm) {
      this.saveModal.open();

      return;
    }

    await this.updateForm();
  }

  async handleContinue(): Promise<void> {
    if (this.formStore.isDataChanged && this.formStore.isAlreadySubmittedForm) {
      this.saveModal.open();

      return;
    }

    await this.formStore.updateFormStatus(FormStatus.FILE_UPLOAD);
  }

  /**
   * Обновление формы
   */
  async updateForm(): Promise<void> {
    await this.formStore.saveEditFormStage();

    this.saveModal.close();
  }

  destroy(): void {
    this.catalogDrawerControllerStore.destroy();

    this._mediatorSubscribers.forEach((s) => s());

    this._dataChangedDisposer();
  }
}

export default EditFormStore;
